import React, { useMemo, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { hasAtLeastOnePermission, hasPermission, createHistory } from "../../sc-utilities";
import { IconButton, Tooltip, Typography, withStyles } from "@material-ui/core";
import { connect } from "react-redux";
import { FindInPage, AssessmentOutlined } from "@material-ui/icons";
import AdminDiagnoseCaseList from "./AdminDiagnoseCaseList";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    label: {
        flexDirection: 'column'
    },
    iconText: {
        fontSize: "10px"
    }
});

export const diagnoseCaseGroupsLookup = (translations) => {
    return {
        "camera": translations["camera"] || "Camera",
        "communication": translations["communication"] || "Communication",
        "control": translations["control"] || "Control",
        "physicalConnection": translations["physical_connection"] || "Physical connection",
        "screen": translations["screen"] || "Screen",
        "sensors": translations["sensors"] || "Sensors",
        "sound": translations["sound"] || "Sound",
        "system": translations["system"] || "System",
        "storage": translations["storage"] || "Storage"
    }
}

export const diagnoseCaseExecutionTypesLookup = (translations) => {
    return {
        "manual": translations["manual"] || "Manual",
        "automatic": translations["automatic"] || "Automatic",
        "assisted": translations["assisted"] || "Assisted"
    }
}

export const allowedDiagnoseCaseParamsPerProductType = {
    work: ['battery_capacity', 'livecall', 'drive_health'],
    mobile: ['battery_capacity', 'livecall', 'battery_discharge'],
    headphone: ['battery_discharge'],
}

const AdminDiagnoseCase = (props) => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, history } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, rowData: null, redirectPath: '' });

    const visible = columns && columns['AdminDiagnoseCase'];

    const groupLookups = diagnoseCaseGroupsLookup(translations);
    const executionTypesLookups = diagnoseCaseExecutionTypesLookup(translations);

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'diagnose_case_load') &&
                        <Tooltip title={translations["diagnose_case_details"] || "Diagnose case details"}>
                            <IconButton
                                component='div'
                                onClick={() => history.push(`/admin/diagnose-case/edit?DiagnoseCaseID=${rowData.DiagnoseCaseID}`)}
                            >
                                <FindInPage />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        hasPermission(permissions, 'diagnose_case_update') ? <Tooltip title={translations["update_params"] || "Update params"}>
                            <IconButton value={rowData.Key} disabled={!allowedDiagnoseCaseParamsPerProductType[rowData.ProductType]?.includes(rowData.Key)} aria-label={translations["update_params"] || "Update params"} onClick={() => redirectHandler(`/admin/diagnose-case/update-case-params`, rowData)}>
                                <AssessmentOutlined />
                            </IconButton>
                        </Tooltip> : ''
                    }
                </>
            },
            {
                title: translations["long_name"] || 'Long name', field: 'LongName',
                hidden: visible ? visible['LongName'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.LongName ? rowData.LongName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["short_name"] || 'Short name', field: 'ShortName',
                hidden: visible ? visible['ShortName'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ShortName ? rowData.ShortName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["execution_type"] || 'Execution type', field: 'ExecutionType',
                hidden: visible ? visible['ExecutionType'] : false,
                filtering: true,
                filterCellStyle: { maxWidth: '150px' },
                lookup: executionTypesLookups,
                defaultFilter: Object.keys(executionTypesLookups),
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ExecutionType ? translations[rowData.ExecutionType] || rowData.ExecutionType : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["product_type"] || 'Product type', field: 'ProductType',
                hidden: visible ? visible['ProductType'] : false,
                filtering: true,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ProductType ? translations[rowData.ProductType] || rowData.ProductType : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["group"] || 'Group', field: 'Group',
                hidden: visible ? visible['Group'] : false,
                filtering: true,
                filterCellStyle: { maxWidth: '150px' },
                lookup: groupLookups,
                defaultFilter: Object.keys(groupLookups),
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Group ? translations[rowData.Group] || rowData.Group : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["visible"] || 'Visible', field: 'Visible',
                hidden: visible ? visible['Visible'] : false,
                filtering: true,
                lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
                defaultFilter: ['0', '1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Visible ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            },
            {
                title: translations["key"] || 'Key', field: 'Key',
                hidden: visible ? visible['Key'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Key ? rowData.Key : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])

    if (!hasAtLeastOnePermission(permissions, ['diagnose_case_load', 'diagnose_case_update'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, rowData) => {
        createHistory();
        setRedirectOptions({ redirect: true, rowData: rowData, redirectPath: path, isSystemDiagnoseCase: true });
    };
    
    if (redirectOptions.redirect) {
        return <Redirect
            to={{
                pathname: redirectOptions.redirectPath.split('?')[0], search: redirectOptions.redirectPath.split('?')[1],
                state: { redirectOptions }
            }}
        />;
    };

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'diagnose_case_list') &&
                <AdminDiagnoseCaseList
                    tableColumns={tableColumns}
                    tableRef={tableRef}
                    history={history}
                />
            }
        </div>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(AdminDiagnoseCase));
